import { Button } from "@/components/ui/button";
import { AutocompleteAddress } from "@/components/address-auto-complete";
import { Trash2 } from "lucide-react";
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { PhoneInputShadcn } from "@/components/ui/phone-input-shadcn";
import { useCustomerAddresses } from "@/api/use-customer-addresses";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

interface SelectedAddress {
  addressComponents: {
    line1: string;
    line2?: string;
    city: string;
    state: string;
    postalCode?: string;
    country: string;
  };
  location: {
    lat: number;
    lng: number;
  };
}

const newAddressSchema = z.object({
  label: z.string().min(1, "Label is required"),
  name: z.string().min(1, "Name is required"),
  phone: z.string().min(1, "Phone number is required"),
  pickupNotes: z.string().optional(),
  deliveryNotes: z.string().optional(),
  address: z.object({
    line1: z.string().min(1),
    line2: z.string().optional(),
    city: z.string().min(1),
    province: z.string().min(1),
    postalCode: z.string().optional(),
    country: z.string().min(1),
    location: z.object({
      lat: z.number(),
      lng: z.number(),
    }),
  }),
});

type NewAddressSchema = z.infer<typeof newAddressSchema>;

export function AddressManagement() {
  const {
    addresses,
    isLoading,
    addAddress: addAddressMutation,
    deleteAddress: deleteAddressMutation,
    updateAddress: updateAddressMutation,
  } = useCustomerAddresses();

  const [selectedNewAddress, setSelectedNewAddress] =
    useState<SelectedAddress | null>(null);

  const form = useForm<NewAddressSchema>({
    resolver: zodResolver(newAddressSchema),
    defaultValues: {
      label: "",
      name: "",
      phone: "",
      pickupNotes: "",
      deliveryNotes: "",
    },
  });

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  if (isLoading) {
    return <div>Loading addresses...</div>;
  }

  return (
    <div className="w-full min-w-[280px] max-w-xl">
      <div className="space-y-4">
        {addresses?.length ? (
          addresses.map((address) => (
            <div
              key={address.id}
              className={cn(
                "flex items-start justify-between p-4 sm:p-6 rounded-xl transition-all",
                "bg-gray-50 hover:bg-gray-100 cursor-pointer border-2",
                address.isPrimary
                  ? "border-blue-500 bg-blue-50/50"
                  : "border-gray-200 hover:border-gray-300"
              )}
              onDoubleClick={() => {
                if (!address.isPrimary) {
                  updateAddressMutation.mutate({
                    addressId: address.id,
                    updates: { isPrimary: true },
                  });
                }
              }}
            >
              <div className="space-y-1">
                <div className="flex items-center gap-2 mb-2">
                  <div className="font-medium">
                    {address.label ?? "Delivers:"}
                  </div>
                  {address.isPrimary && (
                    <span className="text-sm text-blue-600">Primary</span>
                  )}
                </div>
                {address.name && <div className="text-sm">{address.name}</div>}
                {address.phone && (
                  <div className="text-sm pb-2">{address.phone}</div>
                )}
                <div className="text-sm">
                  {address.address.line1}
                  {address.address.line2 && `, ${address.address.line2}`}
                </div>
                <div className="text-sm">{address.address.city},</div>
                <div className="text-sm">
                  {address.address.province} {address.address.postalCode}
                </div>
                <div className="text-sm">{address.address.country}</div>
                {address.pickupNotes && (
                  <div className="text-sm pt-4 ">
                    {address.pickupNotes}
                    <p className="text-xs text-gray-500">
                      (when operator comes to pick up, this note will be shown.)
                    </p>
                  </div>
                )}
                {address.deliveryNotes && (
                  <div className="text-sm pt-4">
                    {address.deliveryNotes}
                    <p className="text-xs text-gray-500">
                      (when operator comes to deliver, this note will be shown.)
                    </p>
                  </div>
                )}
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation();
                  deleteAddressMutation.mutate(address.id);
                }}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))
        ) : (
          <div className="text-center py-8 sm:py-12 border-2 border-dashed rounded-xl">
            <div className="text-gray-500">
              <p className="text-lg font-medium">No addresses yet</p>
              <p className="text-sm mt-1">Add an address to get started</p>
            </div>
          </div>
        )}

        <Button
          onClick={() => setIsCreateDialogOpen(true)}
          className="w-full mt-4"
        >
          Add New Address
        </Button>

        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogContent className="w-[95vw] max-w-[600px] sm:w-full">
            <DialogHeader>
              <DialogTitle>Add New Address</DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Address</Label>
                <AutocompleteAddress
                  onSelectedAddress={(selectedAddress) => {
                    setSelectedNewAddress(selectedAddress);
                    if (selectedAddress) {
                      form.setValue("address", {
                        line1: selectedAddress.addressComponents.line1,
                        line2: selectedAddress.addressComponents.line2,
                        city: selectedAddress.addressComponents.city,
                        province: selectedAddress.addressComponents.state,
                        postalCode:
                          selectedAddress.addressComponents.postalCode ?? "",
                        country: selectedAddress.addressComponents.country,
                        location: selectedAddress.location,
                      });
                    }
                  }}
                  placeholder="Search address"
                />
              </div>

              {selectedNewAddress && (
                <Form {...form}>
                  <form
                    className="flex flex-col gap-4 mt-4"
                    onSubmit={form.handleSubmit((values) => {
                      addAddressMutation.mutate({
                        isPrimary: addresses?.length === 0,
                        label: values.label,
                        name: values.name,
                        phone: values.phone,
                        pickupNotes: values.pickupNotes,
                        deliveryNotes: values.deliveryNotes,
                        address: values.address,
                      });
                      setSelectedNewAddress(null);
                      setIsCreateDialogOpen(false);
                      form.reset();
                    })}
                  >
                    <FormField
                      control={form.control}
                      name="label"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Address Label</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              placeholder="Address label (e.g. Home, Work)"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Contact Name</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="Contact name" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Contact Phone</FormLabel>
                          <FormControl>
                            <PhoneInputShadcn
                              {...field}
                              value={field.value}
                              onChange={(value) => field.onChange(value ?? "")}
                              placeholder="Contact phone number"
                              defaultCountry="CA"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="pickupNotes"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Pickup Notes</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              placeholder="Pickup notes (optional)"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="deliveryNotes"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Delivery Notes</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              placeholder="Delivery notes (optional)"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button type="submit">Add New Address</Button>
                  </form>
                </Form>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
